import { select } from 'd3-selection';
import { line } from 'd3-shape';
import { useEffect, useRef } from 'react';
import { convertBeatTypeNumberToColor, convertBeatTypeNumberToText } from '@/shared/ui';
import {
  calculateConfig,
  calculatePixelSamples,
  calculateSamples,
  CONFIG_ECG,
} from '@/entities/ecg';
import type { ThumbnailBookmarkProps } from './thumbnailBookmark';

/**
 * Renders a bookmarked ECG strip as an SVG thumbnail. Holds the SVG element ref and redraws the waveform whenever the sample data, channels, gain, high-pass filter, dimensions or beat markers change.
 *
 * @param props - ECG samples, channels, display gain, speed, size, high-pass filter and beat markers with their types.
 * @returns An object carrying canvasRef for the SVG element.
 */
const useThumbnailBookmark = ({
  ecgData,
  channels = [2],
  gain,
  speed,
  width,
  height,
  highPass,
  beatMarkers = [],
  hesBeatStatus = [],
}: Pick<
  ThumbnailBookmarkProps,
  | 'ecgData'
  | 'channels'
  | 'gain'
  | 'speed'
  | 'width'
  | 'height'
  | 'highPass'
  | 'beatMarkers'
  | 'hesBeatStatus'
>) => {
  const canvasRef = useRef<SVGSVGElement>(null);

  /**
   * Draws a bookmark's ECG waveform into the thumbnail SVG with a beat-type letter above every beat marker. Picks the requested channels out of the raw data, trims the height when more than one channel is drawn, derives the display config, samples and pixel positions, then clears the SVG and appends one path per channel plus a coloured text label per beat.
   *
   * @param ecgData - Raw samples per available channel.
   * @param channels - One-based channel numbers to draw; nothing is drawn when empty.
   * @param beatMarkers - Sample offsets of the beats to label.
   * @param hesBeatStatus - Beat type per marker, driving each label's text and colour.
   * @returns Nothing.
   */
  const drawStrip = (
    ecgData: number[][],
    channels: number[] = [],
    beatMarkers: number[] = [],
    hesBeatStatus: (string | number)[] = [],
  ) => {
    if (channels.length === 0) {
      return;
    }
    const countChannels = ecgData.length;
    const formatChannels = channels.map((x) => x - 1);
    const drawEcgData: number[][] = [];
    if (countChannels > 1) {
      formatChannels.forEach((x) => {
        drawEcgData.push(ecgData[x]);
      });
    } else {
      drawEcgData.push(ecgData[0]);
    }
    let formatHeight = height;
    if (drawEcgData.length > 1) {
      formatHeight -= 10;
    }
    // calculateConfig's JSDoc param type (BeatHR/helper, not yet converted) also
    // declares a pHFF field this call site never passed.
    const config = calculateConfig({
      pGain: gain,
      pSpeed: speed,
      width,
      height: formatHeight,
      adcGain: CONFIG_ECG.gain,
      ecgData: drawEcgData,
      samplingRate: CONFIG_ECG.samplingFrequency,
      pLFF: highPass,
    } as any);
    const samples = calculateSamples(drawEcgData, config);
    const pixelSamples = calculatePixelSamples(samples, config);
    const { indexToPix } = config;
    const pixelBeats = beatMarkers.map((beatMarker) => Math.floor(beatMarker * indexToPix));
    if (canvasRef.current) {
      const svg = select(canvasRef.current);
      svg.selectAll('*').remove();
      const lineFunc = line<{ x: number; y: number }>()
        .x((d) => d.x)
        .y((d) => d.y);
      pixelSamples.forEach((displayedSamples: any) => {
        svg
          .append('path')
          .attr('d', lineFunc(displayedSamples))
          .attr('stroke', '#1D2F47')
          .attr('stroke-width', '1.2px')
          .attr('fill', 'none');
      });
      pixelBeats.forEach((pixelBeat, index) => {
        svg
          .append('text')
          .attr('x', pixelBeat)
          .attr('y', 10)
          .attr('text-anchor', 'middle')
          .style('font-size', '10px')
          .style('font-weight', '600')
          .style('fill', convertBeatTypeNumberToColor(hesBeatStatus[index] as number))
          .text(convertBeatTypeNumberToText(hesBeatStatus[index] as number));
      });
    }
  };

  useEffect(() => {
    if (ecgData?.length > 0) {
      drawStrip(ecgData, channels, beatMarkers, hesBeatStatus);
    }
  }, [
    ecgData,
    JSON.stringify(channels),
    gain,
    highPass,
    width,
    height,
    beatMarkers,
    hesBeatStatus,
  ]);

  return { canvasRef };
};

export default useThumbnailBookmark;
